import { useContext } from 'react';

import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';

import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults, WaitlistStrings } from '../../../constants';
import { useResendInvitation } from '../../../lib/user/hooks/useResendInvitation';
import { useInviteWaitlistEntry, useWaitlist, waitlistErrorSchema, WaitlistEntry } from '../../../lib/waitlist/useWaitlist';

function status(entry: WaitlistEntry): string {
  if (entry.unavailable) return WaitlistStrings.UNAVAILABLE;
  if (entry.verified) return WaitlistStrings.REGISTERED;
  return entry.invitedAt && entry.userId ? WaitlistStrings.INVITED : WaitlistStrings.PENDING;
}

export default function WaitlistModule() {
  const [{ page, search }, setQuery] = useQueryParams({ page: NumberParam, search: StringParam });
  const pageIndex = typeof page === 'number' && Number.isInteger(page) && page >= 0 ? page : 0;
  const { data, isLoading, isFetching, isError, refetch } = useWaitlist(pageIndex, search ?? '');
  const invite = useInviteWaitlistEntry();
  const resend = useResendInvitation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [, setAlert] = useContext(AlertSnackbarContext);
  const searchEmail = useDebouncedCallback((value: string) => setQuery({ page: 0, search: value }), Defaults.SEARCH_DEBOUNCE_MS);
  const busy = invite.isLoading || resend.isLoading;
  const onError = (error: unknown) => {
    const parsed = waitlistErrorSchema.safeParse(error);
    setAlert({ message: parsed.success ? parsed.data.message : WaitlistStrings.INVITE_FAILED, severity: 'error' });
  };
  const onInvite = (entry: WaitlistEntry) =>
    invite.mutate(entry.id, {
      onSuccess: (result) =>
        setAlert({
          message: result.verified ? WaitlistStrings.ALREADY_REGISTERED : WaitlistStrings.INVITE_SUCCESS,
          severity: 'success',
        }),
      onError,
    });
  const onResend = (userId: string) =>
    resend.mutate(userId, {
      onSuccess: async () => {
        await client.invalidateQueries(['getWaitlist']);
        setAlert({ message: WaitlistStrings.INVITE_SUCCESS, severity: 'success' });
      },
      onError,
    });

  return (
    <Stack spacing={3} sx={{ p: 3, flex: 1, minWidth: 0 }}>
      <Typography variant="h5">{WaitlistStrings.TITLE}</Typography>
      <Typography>{WaitlistStrings.DESCRIPTION}</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <SearchField name="waitlist-search" label={WaitlistStrings.SEARCH} searchString={search ?? ''} onSearch={searchEmail} />
        <Button onClick={() => void refetch()} disabled={isFetching || busy}>
          {WaitlistStrings.REFRESH}
        </Button>
      </Stack>
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Alert severity="error" action={<Button onClick={() => void refetch()}>{WaitlistStrings.RETRY}</Button>}>
          {WaitlistStrings.LOAD_FAILED}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table aria-label={WaitlistStrings.TITLE}>
              <TableHead>
                <TableRow>
                  {[
                    WaitlistStrings.EMAIL,
                    WaitlistStrings.REQUESTED,
                    WaitlistStrings.INVITED_AT,
                    WaitlistStrings.STATUS,
                    WaitlistStrings.ACTIONS,
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>{WaitlistStrings.EMPTY}</TableCell>
                  </TableRow>
                )}
                {data?.entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.email}</TableCell>
                    <TableCell>{new Date(entry.created).toLocaleString()}</TableCell>
                    <TableCell>{entry.invitedAt ? new Date(entry.invitedAt).toLocaleString() : null}</TableCell>
                    <TableCell>{status(entry)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {!entry.verified &&
                          !entry.unavailable &&
                          (entry.invitedAt && entry.userId ? (
                            <LoadingButton
                              loading={resend.isLoading && resend.variables === entry.userId}
                              disabled={busy}
                              onClick={() => entry.userId && onResend(entry.userId)}
                            >
                              {WaitlistStrings.RESEND}
                            </LoadingButton>
                          ) : (
                            <LoadingButton
                              loading={invite.isLoading && invite.variables === entry.id}
                              disabled={busy}
                              onClick={() => onInvite(entry)}
                            >
                              {WaitlistStrings.INVITE}
                            </LoadingButton>
                          ))}
                        {entry.userId && (
                          <Button onClick={() => navigate(`/cms/users/${entry.userId}`)}>{WaitlistStrings.VIEW_USER}</Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography>
              {WaitlistStrings.TOTAL} {data?.total ?? 0}
            </Typography>
            {!!data?.total && (
              <Pagination
                current={pageIndex + 1}
                total={Math.ceil(data.total / Defaults.LIST_PAGE_SIZE)}
                onPageChange={(next) => setQuery({ page: next - 1 })}
              />
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}
